import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Input } from 'src/@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/@/components/ui/select';
import MenuDatas2 from 'src/components/MenuDatas2';
import Iconify from 'src/components/Iconify';
import v2_getSpentChatsDetails_action from 'src/actions/v2/cliente/v2.getSpentChatsDetails';
import { validaLabelMenuDatas } from 'src/utils/functions';
import { CONSUMO_COMPONENT_DESIGN } from './ConsumoComponent.design';

const D = CONSUMO_COMPONENT_DESIGN.details;

const STATUS_BADGE = {
  3: { wrap: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  2: { wrap: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  1: { wrap: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  [-1]: { wrap: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  default: { wrap: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
};

const CHART_PALETTE = ['#7C3AED', '#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#818cf8'];

function parseDateParam(value, fallback) {
  if (!value) return fallback;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? fallback : date;
}

export function ConsumoConversas({ cliente }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inicio, setInicio] = useState(() =>
    parseDateParam(searchParams.get('inicio'), new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  );
  const [final, setFinal] = useState(() =>
    parseDateParam(searchParams.get('final'), new Date())
  );
  const [periodo, setPeriodo] = useState(searchParams.get('periodo') || 'mes_atual');
  const [loading, setLoading] = useState(true);
  const [chatsDetails, setChatsDetails] = useState([]);
  const [filteredChatsDetails, setFilteredChatsDetails] = useState([]);
  const [filters, setFilters] = useState({
    templateName: '',
    priceCategory: '',
    number: '',
    userName: '',
    status: '',
  });

  async function fetchDetails(startDate, endDate, periodoArg) {
    if (!cliente?._id) return;
    try {
      setLoading(true);
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      const querys = `?inicio=${start}&final=${end}&periodo=${periodoArg}`;
      const response = await v2_getSpentChatsDetails_action(cliente._id, querys);
      const data =
        response?.data?.error === false && Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      setChatsDetails(data);
      setFilteredChatsDetails(data);
      setFilters({
        templateName: '',
        priceCategory: '',
        number: '',
        userName: '',
        status: '',
      });
    } catch (error) {
      console.error(error);
      setChatsDetails([]);
      setFilteredChatsDetails([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!cliente?._id) return;
    fetchDetails(inicio, final, periodo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?._id]);

  const uniqueTemplates = useMemo(
    () =>
      chatsDetails
        .map((item) => item.templateName)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort(),
    [chatsDetails]
  );

  const uniquePriceCategories = useMemo(
    () =>
      chatsDetails
        .map((item) => item.price_category)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort(),
    [chatsDetails]
  );

  const uniqueUserNames = useMemo(
    () =>
      chatsDetails
        .map((item) => item.userName)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort(),
    [chatsDetails]
  );

  const uniqueStatuses = useMemo(
    () =>
      chatsDetails
        .map((item) => item.ackLabel)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort(),
    [chatsDetails]
  );

  const deliveryRates = useMemo(() => {
    const total = chatsDetails.length;
    if (total === 0) return { sent: 0, delivered: 0, read: 0, error: 0 };
    const sent = chatsDetails.filter((item) => item.ack >= 1).length;
    const delivered = chatsDetails.filter((item) => item.ack >= 2).length;
    const read = chatsDetails.filter((item) => item.ack === 3).length;
    const error = chatsDetails.filter((item) => item.ack === -1).length;
    return {
      sent: ((sent / total) * 100).toFixed(1),
      delivered: ((delivered / total) * 100).toFixed(1),
      read: ((read / total) * 100).toFixed(1),
      error: ((error / total) * 100).toFixed(1),
    };
  }, [chatsDetails]);

  const chartDataCategories = useMemo(() => {
    const summary = {};
    chatsDetails.forEach((item) => {
      const category = item.price_category || 'Sem categoria';
      summary[category] = (summary[category] || 0) + 1;
    });
    return Object.entries(summary)
      .map(([name, value], index) => ({
        name,
        value,
        fill: CHART_PALETTE[index % CHART_PALETTE.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [chatsDetails]);

  const chartDataTemplates = useMemo(() => {
    const summary = {};
    chatsDetails.forEach((item) => {
      const template = item.templateName || 'Sem nome';
      summary[template] = (summary[template] || 0) + 1;
    });
    return Object.entries(summary)
      .map(([template, value]) => ({
        name: template.length > 18 ? `${template.substring(0, 18)}…` : template,
        fullName: template,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [chatsDetails]);

  const chartDataUsers = useMemo(() => {
    const summary = {};
    chatsDetails.forEach((item) => {
      const userName = item.userName || 'Sem nome';
      summary[userName] = (summary[userName] || 0) + 1;
    });
    return Object.entries(summary)
      .map(([userName, value]) => ({
        name: userName.length > 18 ? `${userName.substring(0, 18)}…` : userName,
        fullName: userName,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [chatsDetails]);

  const chartDataStatus = useMemo(() => {
    const colors = {
      Enviado: '#f59e0b',
      Entregue: '#3b82f6',
      Lido: '#10b981',
      Erro: '#ef4444',
      Pendente: '#6b7280',
    };
    const summary = {};
    chatsDetails.forEach((item) => {
      const status = item.ackLabel || 'Pendente';
      summary[status] = (summary[status] || 0) + 1;
    });
    return Object.entries(summary)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({
        name,
        value,
        fill: colors[name] || '#7C3AED',
      }));
  }, [chatsDetails]);

  const stats = useMemo(() => {
    const total = chatsDetails.length;
    const uniqueTemplatesCount = new Set(chatsDetails.map((item) => item.templateName)).size;
    const uniqueUsersCount = new Set(chatsDetails.map((item) => item.userName)).size;
    return {
      total,
      uniqueTemplates: uniqueTemplatesCount,
      uniqueUsers: uniqueUsersCount,
    };
  }, [chatsDetails]);

  function handleFilterChange(filterName, value) {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);

    let filtered = [...chatsDetails];
    if (newFilters.templateName && newFilters.templateName !== 'all') {
      filtered = filtered.filter((item) => item.templateName === newFilters.templateName);
    }
    if (newFilters.priceCategory && newFilters.priceCategory !== 'all') {
      filtered = filtered.filter((item) => item.price_category === newFilters.priceCategory);
    }
    if (newFilters.number) {
      filtered = filtered.filter((item) => item.number?.includes(newFilters.number));
    }
    if (newFilters.userName && newFilters.userName !== 'all') {
      filtered = filtered.filter((item) => item.userName === newFilters.userName);
    }
    if (newFilters.status && newFilters.status !== 'all') {
      filtered = filtered.filter((item) => item.ackLabel === newFilters.status);
    }
    setFilteredChatsDetails(filtered);
  }

  function handleClearFilters() {
    setFilters({
      templateName: '',
      priceCategory: '',
      number: '',
      userName: '',
      status: '',
    });
    setFilteredChatsDetails(chatsDetails);
  }

  function syncQueryAndFetch(startArg, endArg, periodoArg) {
    const start = startArg ?? inicio;
    const end = endArg ?? final;
    const p = periodoArg !== undefined ? periodoArg : periodo;
    setInicio(start);
    setFinal(end);
    setPeriodo(p);
    setSearchParams({
      inicio: start.toISOString().split('T')[0],
      final: end.toISOString().split('T')[0],
      periodo: p,
    });
    fetchDetails(start, end, p);
  }

  const filtersIdle =
    (!filters.templateName || filters.templateName === 'all') &&
    (!filters.priceCategory || filters.priceCategory === 'all') &&
    !filters.number &&
    (!filters.userName || filters.userName === 'all') &&
    (!filters.status || filters.status === 'all');

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.topbar}>
            <div className={D.topbarIdentity}>
              <button
                type="button"
                className={D.backButton}
                onClick={() => navigate('/dashboard/conta/consumo')}
                aria-label="Voltar para Consumo"
              >
                <Iconify icon="eva:arrow-back-outline" width={18} />
              </button>
              <div className={D.iconTile}>
                <Icon icon="solar:chat-round-money-bold-duotone" width={22} height={22} />
              </div>
              <div className="min-w-0">
                <h1 className={D.title}>Consumo em Conversas</h1>
                <p className={D.subtitle}>
                  Detalhes de templates enviados · {validaLabelMenuDatas(periodo, inicio, final)}
                </p>
              </div>
            </div>
            <div className={D.topbarActions}>
              <MenuDatas2
                inicial={inicio}
                final={final}
                content={validaLabelMenuDatas(periodo, inicio, final)}
                changeInicial={(newValue) => setInicio(newValue)}
                changeFinal={(newValue) => setFinal(newValue)}
                onSelect={(value) => setPeriodo(value)}
                onPesquisar={(startArg, endArg, periodoArg) => {
                  const s = startArg ?? inicio;
                  const e = endArg ?? final;
                  const p = periodoArg !== undefined ? periodoArg : 'null';
                  syncQueryAndFetch(s, e, p);
                }}
                autoLoad={false}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`${D.container} ${D.pageBody}`}>
        <div className={D.stats}>
          <div className={D.statCard}>
            <div className={D.statIcon}>
              <Iconify icon="eva:message-circle-outline" width={20} />
            </div>
            <div>
              <p className={D.statValue}>{loading ? '—' : stats.total}</p>
              <p className={D.statLabel}>Mensagens</p>
            </div>
          </div>
          <div className={D.statCard}>
            <div className={D.statIcon}>
              <Iconify icon="eva:file-text-outline" width={20} />
            </div>
            <div>
              <p className={D.statValue}>{loading ? '—' : stats.uniqueTemplates}</p>
              <p className={D.statLabel}>Templates</p>
            </div>
          </div>
          <div className={D.statCard}>
            <div className={D.statIcon}>
              <Iconify icon="eva:people-outline" width={20} />
            </div>
            <div>
              <p className={D.statValue}>{loading ? '—' : stats.uniqueUsers}</p>
              <p className={D.statLabel}>Usuários</p>
            </div>
          </div>
          <div className={D.statCard}>
            <div className={D.statIcon}>
              <Iconify icon="eva:checkmark-circle-2-outline" width={20} />
            </div>
            <div>
              <p className={D.statValue}>{loading ? '—' : `${deliveryRates.read}%`}</p>
              <p className={D.statLabel}>Taxa de leitura</p>
            </div>
          </div>
        </div>

        <div className={D.contentGrid}>
          <div className={D.chartsCol}>
            {chartDataCategories.length > 0 && (
              <div className={D.chartCard}>
                <p className={D.chartTitle}>Por categoria</p>
                <div className={D.chartBox}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartDataCategories}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={72}
                        dataKey="value"
                        stroke="none"
                      >
                        {chartDataCategories.map((entry, index) => (
                          <Cell key={`cat-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {chartDataTemplates.length > 0 && (
              <div className={D.chartCard}>
                <p className={D.chartTitle}>Top 5 templates</p>
                <div className={D.chartBox}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartDataTemplates} layout="vertical" margin={{ left: 4, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        tick={{ fontSize: 10, fill: '#52525b' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip
                        formatter={(value, _name, props) => [value, props?.payload?.fullName || '']}
                      />
                      <Bar dataKey="value" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {chartDataUsers.length > 0 && (
              <div className={D.chartCard}>
                <p className={D.chartTitle}>Top 5 usuários</p>
                <div className={D.chartBox}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartDataUsers} layout="vertical" margin={{ left: 4, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        tick={{ fontSize: 10, fill: '#52525b' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip
                        formatter={(value, _name, props) => [value, props?.payload?.fullName || '']}
                      />
                      <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {chartDataStatus.length > 0 && (
              <div className={D.chartCard}>
                <p className={D.chartTitle}>Taxa de entrega</p>
                <div className="flex gap-3 items-center">
                  <div className="w-1/2 h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartDataStatus}
                          cx="50%"
                          cy="50%"
                          outerRadius={58}
                          dataKey="value"
                          stroke="none"
                        >
                          {chartDataStatus.map((entry, index) => (
                            <Cell key={`status-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-1/2">
                    <div className={D.rateGrid}>
                      <div className={`${D.rateItem} border-amber-100 bg-amber-50`}>
                        <div className={`${D.rateValue} text-amber-600`}>{deliveryRates.sent}%</div>
                        <div className={`${D.rateLabel} text-amber-700`}>Enviado</div>
                      </div>
                      <div className={`${D.rateItem} border-blue-100 bg-blue-50`}>
                        <div className={`${D.rateValue} text-blue-600`}>{deliveryRates.delivered}%</div>
                        <div className={`${D.rateLabel} text-blue-700`}>Entregue</div>
                      </div>
                      <div className={`${D.rateItem} border-green-100 bg-green-50`}>
                        <div className={`${D.rateValue} text-green-600`}>{deliveryRates.read}%</div>
                        <div className={`${D.rateLabel} text-green-700`}>Lido</div>
                      </div>
                      <div className={`${D.rateItem} border-red-100 bg-red-50`}>
                        <div className={`${D.rateValue} text-red-600`}>{deliveryRates.error}%</div>
                        <div className={`${D.rateLabel} text-red-700`}>Erro</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && stats.total === 0 && (
              <div className={`${D.chartCard} ${D.empty}`}>
                <div className={D.emptyIcon}>
                  <Icon icon="solar:chart-2-bold-duotone" width={28} height={28} />
                </div>
                <p className={D.emptyTitle}>Sem dados para gráficos</p>
                <p className={D.emptySub}>Não há envios neste período.</p>
              </div>
            )}
          </div>

          <div className={D.tableCol}>
            <div className={D.panel}>
              <div className={D.filters}>
                <div>
                  <span className={D.filterLabel}>Template</span>
                  <Select
                    value={filters.templateName || 'all'}
                    onValueChange={(value) => handleFilterChange('templateName', value)}
                  >
                    <SelectTrigger className={D.filterSelect}>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniqueTemplates.map((template) => (
                        <SelectItem key={template} value={template}>
                          {template}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className={D.filterLabel}>Categoria</span>
                  <Select
                    value={filters.priceCategory || 'all'}
                    onValueChange={(value) => handleFilterChange('priceCategory', value)}
                  >
                    <SelectTrigger className={D.filterSelect}>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniquePriceCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className={D.filterLabel}>Número</span>
                  <Input
                    value={filters.number}
                    onChange={(e) => handleFilterChange('number', e.target.value)}
                    placeholder="Buscar..."
                    className={D.filterInput}
                  />
                </div>
                <div>
                  <span className={D.filterLabel}>Usuário</span>
                  <Select
                    value={filters.userName || 'all'}
                    onValueChange={(value) => handleFilterChange('userName', value)}
                  >
                    <SelectTrigger className={D.filterSelect}>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniqueUserNames.map((userName) => (
                        <SelectItem key={userName} value={userName}>
                          {userName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className={D.filterLabel}>Status</span>
                  <Select
                    value={filters.status || 'all'}
                    onValueChange={(value) => handleFilterChange('status', value)}
                  >
                    <SelectTrigger className={D.filterSelect}>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniqueStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  disabled={filtersIdle}
                  className={D.clearBtn}
                >
                  Limpar
                </button>
              </div>

              <div className={D.tableScroll}>
                {loading ? (
                  <div className={D.empty}>
                    <p className={D.emptySub}>Carregando detalhes…</p>
                  </div>
                ) : filteredChatsDetails.length === 0 ? (
                  <div className={D.empty}>
                    <div className={D.emptyIcon}>
                      <Icon icon="solar:inbox-line-bold-duotone" width={28} height={28} />
                    </div>
                    <p className={D.emptyTitle}>
                      {chatsDetails.length === 0 ? 'Nenhum detalhe no período' : 'Nenhum resultado'}
                    </p>
                    <p className={D.emptySub}>
                      {chatsDetails.length === 0
                        ? 'Não há envios de template neste intervalo. Tente outro período.'
                        : 'Ajuste ou limpe os filtros para ver os registros.'}
                    </p>
                  </div>
                ) : (
                  <table className={D.table}>
                    <thead className={D.thead}>
                      <tr>
                        <th className={D.th}>Template</th>
                        <th className={D.th}>Categoria</th>
                        <th className={D.th}>Número</th>
                        <th className={D.th}>Usuário</th>
                        <th className={D.th}>Status</th>
                        <th className={D.th}>Envio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChatsDetails.map((item, index) => {
                        const statusStyle = STATUS_BADGE[item.ack] || STATUS_BADGE.default;
                        return (
                          <tr key={item._id || index} className={D.row}>
                            <td className={`${D.td} font-medium`}>
                              {item.templateName || <span className="text-[#a1a1aa]">Sem nome</span>}
                            </td>
                            <td className={`${D.td} text-[#52525b]`}>
                              {item.price_category || <span className="text-[#a1a1aa]">—</span>}
                            </td>
                            <td className={`${D.td} text-[#52525b] font-mono text-xs`}>
                              {item.number || <span className="text-[#a1a1aa] font-sans">—</span>}
                            </td>
                            <td className={`${D.td} text-[#52525b]`}>
                              {item.userName || <span className="text-[#a1a1aa]">—</span>}
                            </td>
                            <td className={D.td}>
                              {item.ackLabel ? (
                                <span className={`${D.badge} ${statusStyle.wrap}`}>
                                  <span className={`${D.badgeDot} ${statusStyle.dot}`} />
                                  {item.ackLabel}
                                </span>
                              ) : (
                                <span className="text-[#a1a1aa]">—</span>
                              )}
                            </td>
                            <td className={D.td}>
                              {item.date ? (
                                <div className="flex flex-col leading-tight">
                                  <span className="font-medium">
                                    {new Date(item.date).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    })}
                                  </span>
                                  <span className="text-[11px] text-[#a1a1aa]">
                                    {new Date(item.date).toLocaleTimeString('pt-BR', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[#a1a1aa]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className={D.footerMeta}>
                {filteredChatsDetails.length === chatsDetails.length
                  ? `${chatsDetails.length} registro${chatsDetails.length === 1 ? '' : 's'}`
                  : `${filteredChatsDetails.length} de ${chatsDetails.length} registros`}
                {stats.total > 0 && (
                  <span className="ml-2 text-[#a1a1aa]">
                    · Entregue {deliveryRates.delivered}% · Erro {deliveryRates.error}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
